// The ticket as the trade screen shares it: the ticket edits it, the chart draws its bust line,
// the keyboard flips its side.

import type { TicketPreview } from "../../sdk";
import { prefs } from "../../state/prefs";
import { createStore } from "../../state/store";

export type Side = "long" | "short";

export type TicketState = {
  side: Side;
  /** Dollars, as typed. */
  margin: string;
  leverage: number;
  useDebt: boolean;
};

export const DEFAULT_LEVERAGE = 50;

export const ticket = createStore<TicketState>({ side: "long", margin: "", leverage: DEFAULT_LEVERAGE, useDebt: false });

/** The SDK's preview of the ticket as it stands, for the chart. */
export const preview = createStore<{ instrumentId: number; preview: TicketPreview } | null>(null);

export function setTicket(next: Partial<TicketState>) {
  ticket.set((state) => ({ ...state, ...next }));
}

/** Leverage remembered per market. */
export function leverageFor(symbol: string, max: number): number {
  const kept = prefs.get().leverage[symbol] ?? DEFAULT_LEVERAGE;
  return Math.max(1, Math.min(max, Math.round(kept)));
}

export function rememberLeverage(symbol: string, leverage: number) {
  prefs.set((all) => ({ ...all, leverage: { ...all.leverage, [symbol]: leverage } }));
}

/** Leverage presets that fit under `max`. */
export function presets(max: number): number[] {
  return [1, 5, 10, 25, 50, 100, 250, 500, 1000].filter((step) => step <= max);
}

/** The slider runs on a log scale: 0..1000 ↔ 1..max. Leverage is a whole number, not money. */
export const toSlider = (leverage: number, max: number) =>
  max <= 1 ? 0 : Math.round((Math.log(Math.max(1, leverage)) / Math.log(max)) * 1000);
export const fromSlider = (position: number, max: number) =>
  Math.max(1, Math.min(max, Math.round(Math.exp((position / 1000) * Math.log(max)))));
